import { ERROR_CODES, HEADERS } from '@ps/constants';
import axios from 'axios';
import { ApiClientError, isApiErrorBody } from './errors.js';
const SAFE_METHODS = new Set(['get', 'head', 'options']);
const RETRY_STATUSES = new Set([408, 425, 429, 502, 503, 504]);
const REFRESH_PATH = '/auth/refresh';
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/** Correlates a browser request with its server log line. */
function newRequestId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        return crypto.randomUUID();
    return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
function toApiClientError(error) {
    if (error instanceof ApiClientError)
        return error;
    if (axios.isAxiosError(error)) {
        const axiosError = error;
        const requestId = axiosError.response?.headers?.[HEADERS.REQUEST_ID] ??
            axiosError.config?.headers?.[HEADERS.REQUEST_ID];
        if (!axiosError.response) {
            const timedOut = axiosError.code === 'ECONNABORTED';
            return new ApiClientError({
                message: timedOut
                    ? 'The request took too long. Check your connection and try again.'
                    : 'Could not reach Purvanchal Sathi. Check your connection and try again.',
                code: ERROR_CODES.SERVICE_UNAVAILABLE,
                status: null,
                requestId,
                isNetworkError: true,
            });
        }
        const { status, data } = axiosError.response;
        if (isApiErrorBody(data)) {
            return new ApiClientError({
                message: data.message,
                code: data.code,
                status,
                errors: data.errors,
                requestId: data.requestId ?? requestId,
            });
        }
        // A response that is not our envelope means a proxy or gateway answered.
        return new ApiClientError({
            message: 'Something went wrong. Please try again.',
            code: status >= 500 ? ERROR_CODES.INTERNAL_ERROR : ERROR_CODES.SERVICE_UNAVAILABLE,
            status,
            requestId,
        });
    }
    return new ApiClientError({
        message: 'Something went wrong. Please try again.',
        code: ERROR_CODES.INTERNAL_ERROR,
    });
}
export function createApiClient(config) {
    const { baseUrl, client, tokenStore, getLanguage, onUnauthenticated, timeoutMs = 20_000, maxRetries = 2, } = config;
    const instance = axios.create({
        baseURL: baseUrl,
        timeout: timeoutMs,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    });
    // --- request: identity, correlation id, locale -----------------------------
    instance.interceptors.request.use((request) => {
        const tokens = tokenStore.read();
        if (tokens?.accessToken) {
            request.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
        }
        if (!request.headers.get(HEADERS.REQUEST_ID)) {
            request.headers.set(HEADERS.REQUEST_ID, newRequestId());
        }
        request.headers.set(HEADERS.APP_CLIENT, client);
        if (getLanguage)
            request.headers.set(HEADERS.LANGUAGE, getLanguage());
        return request;
    });
    /**
     * One refresh at a time. Without this, a screen firing five queries on load
     * would start five refreshes and rotate the refresh token out from under
     * itself.
     */
    let refreshInFlight = null;
    async function refreshSession() {
        const tokens = tokenStore.read();
        if (!tokens?.refreshToken)
            return null;
        try {
            const response = await axios.post(`${baseUrl}${REFRESH_PATH}`, { refreshToken: tokens.refreshToken }, { timeout: timeoutMs, headers: { [HEADERS.APP_CLIENT]: client } });
            const next = response.data.data;
            tokenStore.write(next);
            return next;
        }
        catch {
            return null;
        }
    }
    function endSession() {
        // Only a session that existed can end. An anonymous visitor reading a
        // public page gets 401s from anything that needs an account, and treating
        // those as an expired session threw them at the sign-in screen — which also
        // meant a crawler never saw the public site at all.
        const hadSession = tokenStore.read() !== null;
        tokenStore.clear();
        if (hadSession)
            onUnauthenticated?.();
    }
    // --- response: refresh once, then retry safe requests -----------------------
    instance.interceptors.response.use((response) => response, async (error) => {
        if (!axios.isAxiosError(error) || !error.config) {
            return Promise.reject(toApiClientError(error));
        }
        const request = error.config;
        const status = error.response?.status;
        const method = (request.method ?? 'get').toLowerCase();
        // Expired access token: refresh once and replay the original request.
        if (status === 401 && !request._psRefreshed && !request.url?.includes(REFRESH_PATH)) {
            request._psRefreshed = true;
            refreshInFlight ??= refreshSession().finally(() => {
                refreshInFlight = null;
            });
            const tokens = await refreshInFlight;
            if (!tokens) {
                endSession();
                return Promise.reject(toApiClientError(error));
            }
            request.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
            return instance.request(request);
        }
        // Retry only where a repeat cannot cause a second booking or payment.
        const retryAllowed = request.retry !== false &&
            (SAFE_METHODS.has(method) || Boolean(request.headers.get(HEADERS.IDEMPOTENCY_KEY)));
        const retryable = !error.response || (status !== undefined && RETRY_STATUSES.has(status));
        const attempt = request._psRetryCount ?? 0;
        if (retryAllowed && retryable && attempt < maxRetries) {
            request._psRetryCount = attempt + 1;
            // 400ms, 800ms … with jitter, so a recovering API is not stampeded.
            const backoff = 400 * 2 ** attempt + Math.random() * 200;
            await sleep(backoff);
            return instance.request(request);
        }
        return Promise.reject(toApiClientError(error));
    });
    async function request(method, url, body, options = {}) {
        const { idempotencyKey, retry, headers, ...rest } = options;
        try {
            const outgoing = {
                method,
                url,
                data: body,
                ...rest,
                headers: {
                    ...headers,
                    ...(idempotencyKey ? { [HEADERS.IDEMPOTENCY_KEY]: idempotencyKey } : {}),
                },
            };
            // `retry` rides along on the config and is read back in the interceptor.
            if (retry !== undefined)
                outgoing.retry = retry;
            const response = await instance.request(outgoing);
            return response.data;
        }
        catch (error) {
            throw toApiClientError(error);
        }
    }
    return {
        get: (url, options) => request('get', url, undefined, options),
        post: (url, body, options) => request('post', url, body, options),
        patch: (url, body, options) => request('patch', url, body, options),
        put: (url, body, options) => request('put', url, body, options),
        delete: (url, options) => request('delete', url, undefined, options),
        setTokens: (tokens) => tokenStore.write(tokens),
        clearSession: endSession,
        axios: instance,
    };
}
//# sourceMappingURL=client.js.map